import { Horse } from './horse';
import { LeagueType } from './league';
import { SubscriptionIntervalOptions } from './payment';

export interface User {
  userId: string;
  username: string; // = name of stud = "Gestütname"
  country: string; // Alpha-2 country code according to https://en.wikipedia.org/wiki/ISO_3166-1 (e.g. "DE", "AT", "NL", "US")
  description: string;
  profilePicture?: string; // filename of the profile picture
  pointsBronze?: number; // only for stats, sum of all horses, not persisted
  pointsSilver?: number; // only for stats, sum of all horses, not persisted
  pointsGold?: number; // only for stats, sum of all horses, not persisted
  toplistLeague: LeagueType; // type of toplist the user is located in
  toplistPoints: number; // current sum of all horse points for this toplist season (will be resetted periodically)
  toplistRank: number; // current rank within the toplist league
  toplistPromotion: number; // change between toplists on next season (-1 = move down, 0 = stay, 1 = move up)
  globalToplistPoints: number; // current sum of poiints for the global toplist
  horses?: Horse[];
}

export interface UserInternal extends User {
  role: UserRole;
  firebaseUid: string;
  mollieCustomerId?: string;
  mollieSubscriptionId?: string;
  email: string;
  isEmailVerified: boolean;
  subscription: UserSubscription;
  subscriptionInterval: SubscriptionIntervalOptions; // interval of the subscription in days
  subscriptionPaymentStatus: UserSubscriptionPaymentStatus;
  subscriptionIsPromoActive: boolean;
  subscriptionValidUntil: string;
  subscriptionPromoValidUntil: string;
  paymentHistory: PaymentHistory[];
  registerDate: string;
}

export const isUserInternal = (user: User | UserInternal): user is UserInternal => {
  const maybeUserInternal = user as UserInternal;
  return typeof maybeUserInternal.email === 'string';
};

export enum UserRole {
  User = 'user',
  Admin = 'admin',
  Bot = 'bot',
}

export enum UserSubscription {
  Basic = 'basic',
  Premium = 'premium',
}

export enum UserSubscriptionPaymentStatus {
  None = 'none',
  Pending = 'pending',
  Paid = 'paid',
  Canceled = 'canceled',
  Error = 'error',
}

export enum PaymentHistoryAction {
  RequestedSubscription = 'requestedSubscription',
  CreatedSubscription = 'createdSubscription',
  CanceledSubscription = 'canceledSubscription',
  ExpiredSubscription = 'expiredSubscription',
  ChangedSubscriptionDuration = 'changedSubscriptionDuration',
  RequestedPayment = 'requestedPayment',
  ConfirmedPayment = 'confirmedPayment',
  CanceledPayment = 'canceledPayment',
  ErrorPayment = 'errorPayment',
}

export enum PaymentHistoryTrigger {
  User = 'user',
  Backend = 'backend',
  MollieCallback = 'mollieCallback',
}

export interface PaymentHistory {
  date: Date;
  action: PaymentHistoryAction;
  trigger: PaymentHistoryTrigger;
  notes?: string;
}

/*

Details regarding subscription types, payment states, etc.

State tuple for explanation:
(subscription: UserSubscription, 
 subscriptionPaymentStatus: UserSubscriptionPaymentStatus, 
 subscriptionIsPromoActive: boolean, 
 subscriptionValidUntil: Date, 
 subscriptionPromoValidUntil: Date)

Examples of states and how they are achieved:
- (1) | (Premium, None, true, 2025-09-19, 2025-09-19) | directly after registration on 2025-09-19
- (2) | (Premium, Paid, true, 2025-10-19, 2025-09-19) | (1) -> (2) user pays for premium subscription while having a free trial month
- (3) | (Premium, Canceled, true, 2025-10-19, 2025-09-19) | (2) -> (3) user cancels premium subscription after paying and while having a free trial month
    - (3) -> (2) user starts subscription again after having canceled it, recreates recurring payment without initial payment, results in the same validity dates if extended during the free trial month
- (4) | (Basic, None, false, 2025-09-19, 2025-09-19) | (1) -> (4) free trial month ends, subscription changes to basic
    - (3) -> (4) after premium subscription is canceled, subscription changes to basic
- (5) | (Premium, Paid, false, 2025-12-15, 2025-09-19) | (4) -> (5) on 2025-11-15 user pays for one month of premium subscription
- (6) | (Premium, _, true, 2025-12-15, 2025-12-15) | (4) -> (6) on 2025-11-15 user gets a free trial month for premium subscription (independently of any payments)
- (7) | (Premium, Error, false, 2025-12-19, 2025-09-19) | (5) -> (7) user tries to pay for extension of premium subscription but payment fails, payment status is set to error but premium subscription is still active

*/

export function filterUserInternalToUser(userInternal: UserInternal): User {
  return {
    userId: userInternal.userId,
    username: userInternal.username,
    country: userInternal.country,
    description: userInternal.description,
    profilePicture: userInternal.profilePicture,
    pointsBronze: userInternal.pointsBronze,
    pointsSilver: userInternal.pointsSilver,
    pointsGold: userInternal.pointsGold,
    toplistLeague: userInternal.toplistLeague,
    toplistPoints: userInternal.toplistPoints,
    toplistRank: userInternal.toplistRank,
    toplistPromotion: userInternal.toplistPromotion,
    globalToplistPoints: userInternal.globalToplistPoints,
    horses: userInternal.horses,
  };
}
